/**
 * Landing Screen
 * Marketing landing page for Wealfi
 * URL: https://wealfi.co
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  useWindowDimensions,
  Linking,
  Platform,
  Modal,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';
import { analytics, AnalyticsEvent } from '../../utils/analytics';
import { initialUtmParams } from '../../utils/initialUtm';


// Wealfi Brand Colors
const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

// External links
const LINKS = {
  youtube: 'http://youtube.com/@boubacar.diallo1',
  newsletter: 'https://www.wealfi.co/news',
  linkedin: 'http://linkedin.com/in/boubacardiallo',
};

// Demo profiles for sandbox (basic info for cards)
const demoProfiles = [
  {
    id: 'sophie',
    name: 'Sophie',
    emoji: '👩‍💼',
    job: 'Salariée',
    income: '2 800€/mois',
    pain: "Trop d'abonnements",
    savings: '147€',
  },
  {
    id: 'karim',
    name: 'Karim',
    emoji: '👨‍💻',
    job: 'Freelance',
    income: '3 200€/mois',
    pain: 'Cashflow instable',
    savings: '350€',
  },
  {
    id: 'marie',
    name: 'Marie',
    emoji: '👩‍🔬',
    job: 'Cadre',
    income: '4 500€/mois',
    pain: 'Frais bancaires',
    savings: '210€',
  },
];

// Complete demo data for modal
const demoProfilesData: Record<string, DemoProfileData> = {
  sophie: {
    id: 'sophie',
    name: 'Sophie',
    emoji: '👩‍💼',
    job: 'Salariée CDI',
    age: 29,
    situation: 'Célibataire, locataire',
    income: 2800,
    yearlySavings: 1764,
    score: 62,
    scoreLabel: 'Peut mieux faire',
    accounts: [
      { name: 'Compte courant BNP', type: 'Courant', balance: 1250, rate: 0, issue: null },
      { name: 'Livret A', type: 'Épargne', balance: 8500, rate: 3.0, issue: null },
      { name: 'Crédit conso Cetelem', type: 'Crédit', balance: -4200, rate: 5.8, issue: 'Taux élevé' },
      { name: 'Netflix + Spotify + Disney+', type: 'Abonnement', balance: -38, rate: 0, issue: 'Doublons' },
    ],
    metrics: [
      { label: 'Économies détectées', value: '1 764€/an', icon: '💰' },
      { label: 'Abonnements inutiles', value: '456€/an', icon: '📺' },
      { label: 'Arbitrage possible', value: '168€/an', icon: '🎯' },
      { label: 'Score santé financière', value: '62/100', icon: '📊' },
    ],
    actions: [
      { priority: 1, title: 'Résilier les abonnements en double', impact: '456€/an', difficulty: 'Facile', time: '15 min' },
      { priority: 2, title: 'Rembourser le crédit conso avec le Livret A', impact: '168€/an', difficulty: 'Moyen', time: '1h' },
      { priority: 3, title: 'Ouvrir un LEP (taux 5%)', impact: '170€/an', difficulty: 'Facile', time: '20 min' },
      { priority: 4, title: 'Automatiser les virements épargne', impact: 'Variable', difficulty: 'Facile', time: '10 min' },
    ],
  },
  karim: {
    id: 'karim',
    name: 'Karim',
    emoji: '👨‍💻',
    job: 'Développeur Freelance',
    age: 34,
    situation: 'En couple, propriétaire',
    income: 3200,
    yearlySavings: 4200,
    score: 71,
    scoreLabel: 'Bon potentiel',
    accounts: [
      { name: 'Compte pro Qonto', type: 'Pro', balance: 12400, rate: 0, issue: 'Trésorerie excédentaire' },
      { name: 'Compte perso Boursorama', type: 'Courant', balance: 3200, rate: 0, issue: null },
      { name: 'Livret A', type: 'Épargne', balance: 22950, rate: 3.0, issue: 'Plafond atteint' },
      { name: 'Assurance vie Yomoni', type: 'Investissement', balance: 15000, rate: 0, issue: null },
      { name: 'Prêt immobilier', type: 'Crédit', balance: -180000, rate: 2.1, issue: null },
    ],
    metrics: [
      { label: 'Économies détectées', value: '4 200€/an', icon: '💰' },
      { label: 'Trésorerie à optimiser', value: '12 400€', icon: '🏦' },
      { label: 'URSSAF à provisionner', value: '8 640€', icon: '📋' },
      { label: 'Score santé financière', value: '71/100', icon: '📊' },
    ],
    actions: [
      { priority: 1, title: 'Créer un compte épargne pro rémunéré', impact: '372€/an', difficulty: 'Facile', time: '30 min' },
      { priority: 2, title: 'Automatiser les provisions URSSAF', impact: 'Sérénité', difficulty: 'Moyen', time: '45 min' },
      { priority: 3, title: 'Ouvrir un PER pour défiscaliser', impact: '2 100€/an', difficulty: 'Moyen', time: '2h' },
      { priority: 4, title: 'Rééquilibrer épargne vs investissement', impact: '1 728€/an', difficulty: 'Facile', time: '1h' },
    ],
  },
  marie: {
    id: 'marie',
    name: 'Marie',
    emoji: '👩‍🔬',
    job: 'Cadre Pharma',
    age: 42,
    situation: 'Mariée, 2 enfants, propriétaire',
    income: 4500,
    yearlySavings: 2520,
    score: 78,
    scoreLabel: 'Bonne gestion',
    accounts: [
      { name: 'Compte joint Société Générale', type: 'Courant', balance: 4800, rate: 0, issue: 'Frais élevés' },
      { name: 'Livret A', type: 'Épargne', balance: 22950, rate: 3.0, issue: 'Plafond atteint' },
      { name: 'LDD', type: 'Épargne', balance: 12000, rate: 3.0, issue: 'Plafond atteint' },
      { name: 'PEL 2018', type: 'Épargne', balance: 28000, rate: 1.0, issue: 'Taux faible' },
      { name: 'Assurance vie AXA', type: 'Investissement', balance: 45000, rate: 0, issue: 'Frais 2.5%' },
      { name: 'Prêt immobilier', type: 'Crédit', balance: -95000, rate: 1.8, issue: null },
    ],
    metrics: [
      { label: 'Économies détectées', value: '2 520€/an', icon: '💰' },
      { label: 'Frais bancaires évitables', value: '384€/an', icon: '🏦' },
      { label: 'Frais AV à réduire', value: '1 125€/an', icon: '📉' },
      { label: 'Score santé financière', value: '78/100', icon: '📊' },
    ],
    actions: [
      { priority: 1, title: 'Migrer vers une banque en ligne', impact: '384€/an', difficulty: 'Moyen', time: '2h' },
      { priority: 2, title: 'Transférer AV vers contrat low-cost', impact: '1 125€/an', difficulty: 'Complexe', time: '4h' },
      { priority: 3, title: 'Casser le PEL et réinvestir', impact: '560€/an', difficulty: 'Moyen', time: '2h' },
      { priority: 4, title: 'Ouvrir PEA pour actions européennes', impact: '451€/an', difficulty: 'Moyen', time: '1h' },
    ],
  },
};

interface DemoProfileData {
  id: string;
  name: string;
  emoji: string;
  job: string;
  age: number;
  situation: string;
  income: number;
  yearlySavings: number;
  score: number;
  scoreLabel: string;
  accounts: Array<{
    name: string;
    type: string;
    balance: number;
    rate: number;
    issue: string | null;
  }>;
  metrics: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
  actions: Array<{
    priority: number;
    title: string;
    impact: string;
    difficulty: string;
    time: string;
  }>;
}

// Features list
const features = [
  {
    icon: 'chart-box-outline',
    title: 'Diagnostic en 3 min',
    desc: 'Analyse complète de ta situation financière avec des recommandations personnalisées.',
  },
  {
    icon: 'target',
    title: "Plan d'action concret",
    desc: "Pas de théorie. Des étapes claires, dans l'ordre, adaptées à TON profil.",
  },
  {
    icon: 'robot',
    title: 'Assistant IA 24/7',
    desc: 'Pose tes questions, obtiens des réponses personnalisées à tout moment.',
  },
  {
    icon: 'bell-ring-outline',
    title: 'Alertes opportunités',
    desc: "Wealfi détecte les optimisations possibles et t'alerte automatiquement.",
  },
];

// Testimonials
const testimonials = [
  {
    name: 'Thomas L.',
    role: 'Ingénieur, 31 ans',
    text: "En 3 minutes, Wealfi a détecté 168€/an d'économies sur mon crédit. Je n'aurais jamais trouvé ça seul.",
    savings: '168€/an',
  },
  {
    name: 'Sarah M.',
    role: 'Chef de projet, 29 ans',
    text: "Enfin une app qui me dit QUOI faire, pas juste des graphiques. Mon premier objectif atteint en 2 mois.",
    savings: '2 400€',
  },
];

// Pricing plans
const pricingPlans = [
  {
    name: 'Gratuit',
    price: '0€',
    period: 'pour toujours',
    features: [
      { text: 'Diagnostic complet', included: true },
      { text: "Plan d'action personnalisé", included: true },
      { text: "3 messages/mois avec l'IA", included: true },
      { text: "Suivi d'1 objectif", included: true },
      { text: 'Dashboard patrimoine', included: false },
      { text: 'Alertes opportunités', included: false },
    ],
    cta: 'Commencer gratuitement',
    popular: false,
  },
  {
    name: 'Premium',
    price: '9.99€',
    originalPrice: '12.99€',
    period: '/mois',
    badge: '⭐ Early Access -20%',
    features: [
      { text: 'Tout le Gratuit +', included: true },
      { text: "Chat illimité avec l'IA", included: true },
      { text: 'Objectifs illimités', included: true },
      { text: 'Dashboard patrimoine', included: true },
      { text: 'Alertes opportunités', included: true },
      { text: 'Contenu éducatif premium', included: true },
    ],
    cta: 'Essayer 7 jours gratuit',
    popular: true,
  },
  {
    name: 'Pro',
    price: '19.99€',
    period: '/mois',
    features: [
      { text: 'Tout Premium +', included: true },
      { text: 'Cartographie complète', included: true },
      { text: 'Recommandations fiscales', included: true },
      { text: 'Export PDF rapports', included: true },
      { text: 'Support prioritaire', included: true },
    ],
    cta: 'Bientôt disponible',
    popular: false,
    disabled: true,
  },
];

export default function LandingScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [modalTab, setModalTab] = useState<'overview' | 'accounts' | 'actions'>('overview');
  const scrollRef = useRef<ScrollView>(null);
  const { isLandingDomain, appUrl } = useDomain();

  useWebSEO({
    title: 'Wealfi — Ton assistant financier IA | Diagnostic gratuit',
    description: 'Wealfi analyse tes finances en 3 minutes grâce à l\'IA. Diagnostic personnalisé, assistant 24/7, connexion bancaire sécurisée via Powens. Gratuit pour commencer.',
    keywords: 'assistant financier IA, diagnostic financier, gestion budget, épargne, intelligence artificielle finance, Powens, finances personnelles',
    ogUrl: 'https://wealfi.co/fr',
    ogImage: 'https://wealfi.co/og-images/landing.png',
  });

  // Section refs for scrolling
  const [sectionPositions, setSectionPositions] = useState<Record<string, number>>({});

  // Analytics: scroll depth tracking
  const scrollDepthTracked = useRef<Set<number>>(new Set());
  const pageLoadTime = useRef<number>(Date.now());

  // UTM parameters captured at page load (before React Navigation can strip them)
  const getUtmParams = useCallback(() => ({
    ...initialUtmParams,
  }), []);

  // Track landing page view on mount
  useEffect(() => {
    analytics.initialize();
    const utmParams = getUtmParams();
    analytics.trackEvent(AnalyticsEvent.LANDING_PAGE_VIEWED, {
      ...utmParams,
      referrer: Platform.OS === 'web' ? document.referrer : undefined,
    });

    // Track time spent on unmount
    return () => {
      const timeSpent = Math.floor((Date.now() - pageLoadTime.current) / 1000);
      analytics.trackEvent(AnalyticsEvent.LANDING_TIME_SPENT, {
        seconds: timeSpent,
        ...utmParams,
      });
    };
  }, [getUtmParams]);

  // Scroll depth tracking (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      const thresholds = [25, 50, 75, 100];
      for (const threshold of thresholds) {
        if (scrollPercent >= threshold && !scrollDepthTracked.current.has(threshold)) {
          scrollDepthTracked.current.add(threshold);
          analytics.trackEvent(AnalyticsEvent.LANDING_SCROLL_DEPTH, {
            depth: threshold,
            ...getUtmParams(),
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [getUtmParams]);

  // Track CTA clicks
  const trackCta = useCallback((ctaName: string, section: string) => {
    analytics.trackEvent(AnalyticsEvent.LANDING_CTA_CLICKED, {
      cta_name: ctaName,
      section,
      ...getUtmParams(),
    });
  }, [getUtmParams]);

  // Track nav clicks
  const trackNav = useCallback((navItem: string) => {
    analytics.trackEvent(AnalyticsEvent.LANDING_NAV_CLICKED, {
      nav_item: navItem,
      ...getUtmParams(),
    });
  }, [getUtmParams]);

  const handleGetStarted = () => {
    trackCta('get_started', 'hero');
    // On landing domain (wealfi.co), redirect to app.wealfi.co
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/signup`;
      return;
    }
    navigation.navigate('Auth', { screen: 'SignUp' });
  };

  const handleLogin = () => {
    trackCta('login', 'navbar');
    // On landing domain (wealfi.co), redirect to app.wealfi.co
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/login`;
      return;
    }
    navigation.navigate('Auth', { screen: 'Login' });
  };

  const scrollToSection = (section: string) => {
    // On web, use element position relative to document
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const element = document.getElementById(`section-${section}`);
      if (element) {
        // Get element's position relative to the document
        const elementRect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        // Offset to position section title just below fixed navbar
        const navbarOffset = 20;
        const targetPosition = elementRect.top + scrollTop - navbarOffset;

        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        return;
      }
    }

    // Fallback for native
    const position = sectionPositions[section];
    if (position !== undefined && scrollRef.current) {
      scrollRef.current.scrollTo({ y: position - 20, animated: true });
    }
  };

  const openLink = (url: string) => {
    Linking.openURL(url);
  };

  const openDemoModal = () => {
    if (activeDemo) {
      setModalTab('overview');
      setShowDemoModal(true);
      analytics.trackEvent(AnalyticsEvent.LANDING_DEMO_MODAL_OPENED, {
        profile: activeDemo,
        ...getUtmParams(),
      });
    }
  };

  const closeDemoModal = () => {
    setShowDemoModal(false);
  };

  const handleTabChange = (tab: 'overview' | 'accounts' | 'actions') => {
    setModalTab(tab);
    analytics.trackEvent(AnalyticsEvent.LANDING_DEMO_TAB_CHANGED, {
      tab,
      profile: activeDemo,
      ...getUtmParams(),
    });
  };

  const selectedProfile = activeDemo ? demoProfilesData[activeDemo] : null;

  const formatBalance = (balance: number) => {
    const formatted = Math.abs(balance).toLocaleString('fr-FR');
    return balance < 0 ? `-${formatted}€` : `${formatted}€`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Facile':
        return brandColors.green;
      case 'Moyen':
        return brandColors.orange;
      case 'Complexe':
        return '#EF4444';
      default:
        return brandColors.gray;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return brandColors.green;
    if (score >= 60) return brandColors.orange;
    return '#EF4444';
  };

  // Demo Modal Component
  const renderDemoModal = () => {
    if (!selectedProfile) return null;

    return (
      <Modal
        visible={showDemoModal}
        animationType="slide"
        transparent={true}
        onRequestClose={closeDemoModal}
      >
        <View style={modalStyles.overlay}>
          <View style={[modalStyles.container, isDesktop && modalStyles.containerDesktop]}>
            {/* Demo Banner */}
            <View style={modalStyles.demoBanner}>
              <Text style={modalStyles.demoBannerText}>
                🧪 MODE DÉMO — Profil fictif de {selectedProfile.name}
              </Text>
            </View>

            {/* Header */}
            <View style={modalStyles.header}>
              <View style={modalStyles.profileInfo}>
                <View style={modalStyles.profileAvatar}>
                  <Text style={modalStyles.profileAvatarText}>{selectedProfile.emoji}</Text>
                </View>
                <View>
                  <Text style={modalStyles.profileName}>{selectedProfile.name}, {selectedProfile.age} ans</Text>
                  <Text style={modalStyles.profileJob}>{selectedProfile.job}</Text>
                  <Text style={modalStyles.profileSituation}>{selectedProfile.situation}</Text>
                </View>
              </View>
              <Pressable onPress={closeDemoModal} style={modalStyles.closeButton}>
                <Icon name="close" size={24} color={brandColors.gray} />
              </Pressable>
            </View>

            {/* Tab Navigation */}
            <View style={modalStyles.tabs}>
              <Pressable
                style={[modalStyles.tab, modalTab === 'overview' && modalStyles.tabActive]}
                onPress={() => handleTabChange('overview')}
              >
                <Icon name="chart-box-outline" size={18} color={modalTab === 'overview' ? brandColors.green : brandColors.gray} />
                <Text style={[modalStyles.tabText, modalTab === 'overview' && modalStyles.tabTextActive]}>
                  Vue d'ensemble
                </Text>
              </Pressable>
              <Pressable
                style={[modalStyles.tab, modalTab === 'accounts' && modalStyles.tabActive]}
                onPress={() => handleTabChange('accounts')}
              >
                <Icon name="bank-outline" size={18} color={modalTab === 'accounts' ? brandColors.green : brandColors.gray} />
                <Text style={[modalStyles.tabText, modalTab === 'accounts' && modalStyles.tabTextActive]}>
                  Détail comptes
                </Text>
              </Pressable>
              <Pressable
                style={[modalStyles.tab, modalTab === 'actions' && modalStyles.tabActive]}
                onPress={() => handleTabChange('actions')}
              >
                <Icon name="clipboard-check-outline" size={18} color={modalTab === 'actions' ? brandColors.green : brandColors.gray} />
                <Text style={[modalStyles.tabText, modalTab === 'actions' && modalStyles.tabTextActive]}>
                  Plan d'action
                </Text>
              </Pressable>
            </View>

            {/* Tab Content */}
            <ScrollView style={modalStyles.content} showsVerticalScrollIndicator={false}>
              {modalTab === 'overview' && (
                <View style={modalStyles.overviewContent}>
                  {/* Score Card */}
                  <View style={modalStyles.scoreCard}>
                    <View style={modalStyles.scoreHeader}>
                      <Text style={modalStyles.scoreLabel}>Score santé financière</Text>
                      <View style={[modalStyles.scoreBadge, { backgroundColor: getScoreColor(selectedProfile.score) + '20' }]}>
                        <Text style={[modalStyles.scoreBadgeText, { color: getScoreColor(selectedProfile.score) }]}>
                          {selectedProfile.scoreLabel}
                        </Text>
                      </View>
                    </View>
                    <View style={modalStyles.scoreValueContainer}>
                      <Text style={[modalStyles.scoreValue, { color: getScoreColor(selectedProfile.score) }]}>
                        {selectedProfile.score}
                      </Text>
                      <Text style={modalStyles.scoreMax}>/100</Text>
                    </View>
                    <View style={modalStyles.scoreBar}>
                      <View
                        style={[
                          modalStyles.scoreBarFill,
                          { width: `${selectedProfile.score}%`, backgroundColor: getScoreColor(selectedProfile.score) },
                        ]}
                      />
                    </View>
                  </View>

                  {/* Main Savings Card */}
                  <View style={modalStyles.savingsCard}>
                    <Text style={modalStyles.savingsLabel}>💰 Économies annuelles détectées</Text>
                    <Text style={modalStyles.savingsValue}>
                      {selectedProfile.yearlySavings.toLocaleString('fr-FR')}€<Text style={modalStyles.savingsPeriod}>/an</Text>
                    </Text>
                    <Text style={modalStyles.savingsSubtext}>
                      Soit {Math.round(selectedProfile.yearlySavings / 12)}€/mois en plus dans ta poche
                    </Text>
                  </View>

                  {/* Metrics Grid */}
                  <View style={modalStyles.metricsGrid}>
                    {selectedProfile.metrics.map((metric, index) => (
                      <View key={index} style={modalStyles.metricCard}>
                        <Text style={modalStyles.metricIcon}>{metric.icon}</Text>
                        <Text style={modalStyles.metricValue}>{metric.value}</Text>
                        <Text style={modalStyles.metricLabel}>{metric.label}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {modalTab === 'accounts' && (
                <View style={modalStyles.accountsContent}>
                  <Text style={modalStyles.sectionTitle}>Comptes analysés</Text>
                  {selectedProfile.accounts.map((account, index) => (
                    <View key={index} style={modalStyles.accountCard}>
                      <View style={modalStyles.accountHeader}>
                        <View style={modalStyles.accountInfo}>
                          <Text style={modalStyles.accountName}>{account.name}</Text>
                          <View style={modalStyles.accountTypeBadge}>
                            <Text style={modalStyles.accountTypeText}>{account.type}</Text>
                          </View>
                        </View>
                        <Text style={[
                          modalStyles.accountBalance,
                          account.balance < 0 && modalStyles.accountBalanceNegative
                        ]}>
                          {formatBalance(account.balance)}
                        </Text>
                      </View>
                      <View style={modalStyles.accountDetails}>
                        {account.rate > 0 && (
                          <View style={modalStyles.accountDetailItem}>
                            <Text style={modalStyles.accountDetailLabel}>Taux</Text>
                            <Text style={modalStyles.accountDetailValue}>{account.rate}%</Text>
                          </View>
                        )}
                        {account.issue && (
                          <View style={[modalStyles.accountIssueBadge]}>
                            <Icon name="alert-circle-outline" size={14} color={brandColors.orange} />
                            <Text style={modalStyles.accountIssueText}>{account.issue}</Text>
                          </View>
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {modalTab === 'actions' && (
                <View style={modalStyles.actionsContent}>
                  <Text style={modalStyles.sectionTitle}>Plan d'action personnalisé</Text>
                  <Text style={modalStyles.sectionSubtitle}>
                    4 actions prioritaires pour optimiser les finances de {selectedProfile.name}
                  </Text>
                  {selectedProfile.actions.map((action, index) => (
                    <View key={index} style={modalStyles.actionCard}>
                      <View style={modalStyles.actionPriority}>
                        <Text style={modalStyles.actionPriorityText}>{action.priority}</Text>
                      </View>
                      <View style={modalStyles.actionContent}>
                        <Text style={modalStyles.actionTitle}>{action.title}</Text>
                        <View style={modalStyles.actionMeta}>
                          <View style={modalStyles.actionMetaItem}>
                            <Icon name="cash" size={14} color={brandColors.green} />
                            <Text style={modalStyles.actionImpact}>{action.impact}</Text>
                          </View>
                          <View style={[
                            modalStyles.actionDifficultyBadge,
                            { backgroundColor: getDifficultyColor(action.difficulty) + '20' }
                          ]}>
                            <Text style={[
                              modalStyles.actionDifficultyText,
                              { color: getDifficultyColor(action.difficulty) }
                            ]}>
                              {action.difficulty}
                            </Text>
                          </View>
                          <View style={modalStyles.actionMetaItem}>
                            <Icon name="clock-outline" size={14} color={brandColors.gray} />
                            <Text style={modalStyles.actionTime}>{action.time}</Text>
                          </View>
                        </View>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>

            {/* Footer CTA */}
            <View style={modalStyles.footer}>
              <Text style={modalStyles.footerText}>
                Et toi, combien tu peux économiser ?
              </Text>
              <Pressable style={modalStyles.footerCta} onPress={() => { trackCta('get_started', 'demo_modal'); closeDemoModal(); handleGetStarted(); }}>
                <Text style={modalStyles.footerCtaText}>Faire MON diagnostic gratuit</Text>
                <Icon name="arrow-right" size={18} color={brandColors.white} />
              </Pressable>
              <Text style={modalStyles.footerNote}>
                3 minutes • Sans carte bancaire • Connexion sécurisée
              </Text>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={styles.container}>
      {/* Demo Modal */}
      {renderDemoModal()}

      {/* Fixed Navigation */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <View style={styles.logoContainer}>
          <View style={styles.logoIcon}>
            <Text style={styles.logoEmoji}>🤖</Text>
          </View>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </View>

        {/* Desktop Navigation Links */}
        {isDesktop && (
          <View style={styles.navLinks}>
            <Pressable onPress={() => { trackNav('features'); scrollToSection('features'); }} style={styles.navLink}>
              <Text style={styles.navLinkText}>Fonctionnalités</Text>
            </Pressable>
            <Pressable onPress={() => { trackNav('demo'); scrollToSection('demo'); }} style={styles.navLink}>
              <Text style={styles.navLinkText}>Démo</Text>
            </Pressable>
            <Pressable onPress={() => { trackNav('pricing'); scrollToSection('pricing'); }} style={styles.navLink}>
              <Text style={styles.navLinkText}>Tarifs</Text>
            </Pressable>
            <Pressable onPress={() => { trackNav('tools'); if (Platform.OS === 'web') window.location.href = '/fr/tools'; }} style={styles.navLink}>
              <Text style={styles.navLinkText}>Outils</Text>
            </Pressable>
          </View>
        )}

        <View style={styles.navActions}>
          {isDesktop && (
            <Pressable onPress={handleLogin} style={styles.navLinkButton}>
              <Text style={styles.navLinkText}>Connexion</Text>
            </Pressable>
          )}
          <Pressable onPress={handleGetStarted} style={[styles.ctaButton, !isDesktop && styles.ctaButtonMobile]}>
            <Text style={styles.ctaButtonText}>{isDesktop ? 'Essayer gratuitement' : 'Essayer'}</Text>
          </Pressable>
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        nativeID="landing-scroll-container"
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Hero Section with gradient background */}
        <View style={[styles.heroSection, isDesktop && styles.heroSectionDesktop]}>
          <View style={[styles.heroContent, isDesktop && styles.heroContentDesktop]}>
            {/* Left: Copy */}
            <View style={[styles.heroLeft, isDesktop && styles.heroLeftDesktop]}>
              <View style={styles.heroBadge}>
                <View style={styles.heroBadgeDot} />
                <Text style={styles.heroBadgeText}>+168€/an détectés en moyenne</Text>
              </View>

              <Text style={[styles.heroTitle, isDesktop && styles.heroTitleDesktop]}>
                Ton assistant financier IA qui{' '}
                <Text style={styles.heroTitleAccent}>optimise ton argent</Text>
              </Text>

              <Text style={styles.heroSubtitle}>
                Diagnostic personnalisé en 3 minutes. Plan d'action concret. Découvre
                combien tu peux économiser — sans connecter ta banque.
              </Text>

              <View style={[styles.heroButtons, isDesktop && styles.heroButtonsDesktop]}>
                <Pressable onPress={handleGetStarted} style={styles.primaryButton}>
                  <Text style={styles.primaryButtonText}>
                    Faire mon diagnostic gratuit
                  </Text>
                  <Icon name="arrow-right" size={20} color={brandColors.white} />
                </Pressable>

                <Pressable onPress={() => { trackCta('see_demo', 'hero'); scrollToSection('demo'); }} style={styles.secondaryButton}>
                  <Text style={styles.secondaryButtonText}>Voir une démo</Text>
                </Pressable>
              </View>

              <View style={styles.heroTrust}>
                <View style={styles.trustItem}>
                  <Icon name="check-circle" size={20} color={brandColors.green} />
                  <Text style={styles.trustText}>Gratuit pour démarrer</Text>
                </View>
                <View style={styles.trustItem}>
                  <Icon name="shield-lock" size={20} color={brandColors.green} />
                  <Text style={styles.trustText}>Connexion sécurisée</Text>
                </View>
              </View>
            </View>

            {/* Right: Chat Preview */}
            {isDesktop && (
              <View style={styles.heroRight}>
                <View style={styles.chatPreview}>
                  <View style={styles.chatHeader}>
                    <View style={styles.chatAvatar}>
                      <Text style={styles.chatAvatarText}>🤖</Text>
                    </View>
                    <View>
                      <Text style={styles.chatName}>Wealfi</Text>
                      <View style={styles.chatStatus}>
                        <View style={styles.chatStatusDot} />
                        <Text style={styles.chatStatusText}>En ligne</Text>
                      </View>
                    </View>
                  </View>

                  <View style={styles.chatMessages}>
                    <View style={styles.chatBubbleAi}>
                      <Text style={styles.chatBubbleText}>
                        Salut Thomas ! 👋 J'ai analysé ton profil. Bonne nouvelle : j'ai détecté une opportunité d'arbitrage de <Text style={styles.chatBold}>168€/an</Text>.
                      </Text>
                    </View>

                    <View style={styles.chatBubbleAi}>
                      <Text style={styles.chatBubbleText}>
                        Tu as 8 500€ sur ton Livret A qui rapportent 3%/an, mais tu paies 5.8% sur ton crédit conso...
                      </Text>
                      <View style={styles.chatStats}>
                        <View style={styles.chatStatRow}>
                          <Text style={styles.chatStatLabel}>💰 Gain annuel estimé</Text>
                          <Text style={styles.chatStatValue}>+168€</Text>
                        </View>
                        <View style={styles.chatStatRow}>
                          <Text style={styles.chatStatLabel}>📅 Mensualité libérée</Text>
                          <Text style={styles.chatStatValue}>+14€/mois</Text>
                        </View>
                      </View>
                    </View>

                    <View style={styles.chatBubbleUser}>
                      <Text style={styles.chatBubbleUserText}>
                        Intéressant ! Comment je fais concrètement ?
                      </Text>
                    </View>

                    <View style={styles.chatTyping}>
                      <View style={styles.typingDots}>
                        <View style={[styles.typingDot, styles.typingDot1]} />
                        <View style={[styles.typingDot, styles.typingDot2]} />
                        <View style={[styles.typingDot, styles.typingDot3]} />
                      </View>
                      <Text style={styles.typingText}>Wealfi écrit...</Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Problem Section */}
        <View style={styles.problemSection}>
          <Text style={styles.sectionTitle}>
            Ton argent dort. Ton temps manque.
          </Text>
          <Text style={styles.sectionSubtitle}>
            Tu sais que tu devrais optimiser tes finances. Mais entre le boulot,
            la vie perso et l'info overload...
          </Text>

          <View style={[styles.problemGrid, isDesktop && styles.problemGridDesktop]}>
            {[
              { emoji: '😰', title: 'Tu procrastines', desc: '"Je verrai ça ce week-end"... depuis 6 mois.' },
              { emoji: '🤯', title: 'Info overload', desc: "YouTube, blogs, Reddit... et au final tu ne fais rien." },
              { emoji: '💸', title: "Tu perds de l'argent", desc: 'Inflation, frais cachés, épargne mal placée.' },
            ].map((item, index) => (
              <View key={index} style={styles.problemCard}>
                <Text style={styles.problemEmoji}>{item.emoji}</Text>
                <Text style={styles.problemCardTitle}>{item.title}</Text>
                <Text style={styles.problemCardDesc}>{item.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Features Section */}
        <View
          nativeID="section-features"
          style={styles.featuresSection}
          onLayout={(e) => setSectionPositions(prev => ({ ...prev, features: e.nativeEvent.layout.y }))}
        >
          <Text style={styles.sectionTitle}>
            Wealfi fait le travail <Text style={styles.titleAccent}>pour toi</Text>
          </Text>
          <Text style={styles.sectionSubtitle}>
            Pas de théorie. Pas de jargon. Juste des actions concrètes adaptées à ta situation.
          </Text>

          <View style={[styles.featuresGrid, isDesktop && styles.featuresGridDesktop]}>
            {features.map((feature, index) => (
              <View key={index} style={[styles.featureCard, isDesktop && styles.featureCardDesktop]}>
                <View style={styles.featureIconContainer}>
                  <Icon name={feature.icon as any} size={32} color={brandColors.green} />
                </View>
                <Text style={styles.featureTitle}>{feature.title}</Text>
                <Text style={styles.featureDesc}>{feature.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Demo Section */}
        <View
          nativeID="section-demo"
          style={styles.demoSection}
          onLayout={(e) => setSectionPositions(prev => ({ ...prev, demo: e.nativeEvent.layout.y }))}
        >
          <Text style={styles.sectionTitle}>
            Pas encore prêt ? <Text style={styles.titleAccentOrange}>Teste avec un profil fictif</Text>
          </Text>
          <Text style={styles.sectionSubtitle}>
            Choisis un profil similaire au tien et vois ce que Wealfi détecte — sans partager tes données.
          </Text>

          <View style={[styles.demoGrid, isDesktop && styles.demoGridDesktop]}>
            {demoProfiles.map((profile) => (
              <Pressable
                key={profile.id}
                onPress={() => setActiveDemo(profile.id)}
                style={[styles.demoCard, activeDemo === profile.id && styles.demoCardActive]}
              >
                <View style={styles.demoCardHeader}>
                  <View style={styles.demoAvatar}>
                    <Text style={styles.demoAvatarText}>{profile.emoji}</Text>
                  </View>
                  <View>
                    <Text style={styles.demoName}>{profile.name}</Text>
                    <Text style={styles.demoJob}>{profile.job} • {profile.income}</Text>
                  </View>
                </View>

                <View style={styles.demoProblem}>
                  <Text style={styles.demoProblemLabel}>Problème détecté</Text>
                  <Text style={styles.demoProblemValue}>{profile.pain}</Text>
                </View>

                <View style={styles.demoSavings}>
                  <Text style={styles.demoSavingsLabel}>Économies détectées</Text>
                  <Text style={styles.demoSavingsValue}>{profile.savings}/mois</Text>
                </View>
              </Pressable>
            ))}
          </View>

          <Pressable
            style={[styles.primaryButton, !activeDemo && styles.buttonDisabled]}
            disabled={!activeDemo}
            onPress={openDemoModal}
          >
            <Text style={styles.primaryButtonText}>
              {activeDemo
                ? `Voir le diagnostic de ${demoProfiles.find((p) => p.id === activeDemo)?.name}`
                : 'Sélectionne un profil'}
            </Text>
          </Pressable>
        </View>

        {/* Testimonials Section */}
        <View style={styles.testimonialsSection}>
          <Text style={styles.sectionTitle}>Ce qu'ils ont découvert avec Wealfi</Text>

          <View style={[styles.testimonialsGrid, isDesktop && styles.testimonialsGridDesktop]}>
            {testimonials.map((t, index) => (
              <View key={index} style={[styles.testimonialCard, isDesktop && styles.testimonialCardDesktop]}>
                <View style={styles.testimonialHeader}>
                  <View style={styles.testimonialAvatar}>
                    <Text style={styles.testimonialAvatarText}>{t.name.charAt(0)}</Text>
                  </View>
                  <View style={styles.testimonialInfo}>
                    <Text style={styles.testimonialName}>{t.name}</Text>
                    <Text style={styles.testimonialRole}>{t.role}</Text>
                  </View>
                  <View style={styles.testimonialSavings}>
                    <Text style={styles.testimonialSavingsLabel}>Économisé</Text>
                    <Text style={styles.testimonialSavingsValue}>{t.savings}</Text>
                  </View>
                </View>
                <Text style={styles.testimonialText}>"{t.text}"</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Trust Section */}
        <View style={styles.trustSection}>
          <Text style={styles.sectionTitle}>
            Tes données, <Text style={styles.titleAccent}>tes règles</Text>
          </Text>
          <Text style={styles.sectionSubtitle}>
            Transparence totale sur ce qu'on fait (et ne fait pas) avec tes informations.
          </Text>

          <View style={[styles.trustGrid, isDesktop && styles.trustGridDesktop]}>
            {[
              {
                icon: 'lock',
                title: 'Connexion sécurisée',
                desc: 'Tes échanges sont protégés par chiffrement SSL/TLS',
              },
              {
                icon: 'hand-back-right',
                title: 'Données jamais vendues',
                desc: 'Pas de pub ciblée, pas de revente à des tiers',
              },
              {
                icon: 'bank-outline',
                title: 'Connexion bancaire optionnelle',
                desc: 'Via Powens, établissement de paiement agréé ACPR (n°16948)',
              },
              {
                icon: 'delete-outline',
                title: 'Supprime ton compte à tout moment',
                desc: 'Un clic pour supprimer toutes tes données',
              },
            ].map((card, index) => (
              <View key={index} style={[styles.trustCard, isDesktop && styles.trustCardDesktop]}>
                <View style={styles.trustCardIcon}>
                  <Icon name={card.icon as any} size={28} color={brandColors.green} />
                </View>
                <Text style={styles.trustCardTitle}>{card.title}</Text>
                <Text style={styles.trustCardDesc}>{card.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Pricing Section */}
        <View
          nativeID="section-pricing"
          style={styles.pricingSection}
          onLayout={(e) => setSectionPositions(prev => ({ ...prev, pricing: e.nativeEvent.layout.y }))}
        >
          <Text style={styles.sectionTitle}>Des tarifs simples et transparents</Text>
          <Text style={styles.sectionSubtitle}>
            Commence gratuitement. Passe Premium quand tu es prêt.
          </Text>

          <View style={[styles.pricingGrid, isDesktop && styles.pricingGridDesktop]}>
            {pricingPlans.map((plan, index) => (
              <View key={index} style={[styles.pricingCard, isDesktop && styles.pricingCardDesktop, plan.popular && styles.pricingCardPopular]}>
                {plan.badge && (
                  <View style={styles.pricingBadge}>
                    <Text style={styles.pricingBadgeText}>{plan.badge}</Text>
                  </View>
                )}

                <Text style={styles.pricingName}>{plan.name}</Text>

                <View style={styles.pricingPriceContainer}>
                  {plan.originalPrice && (
                    <Text style={styles.pricingOriginalPrice}>{plan.originalPrice}</Text>
                  )}
                  <Text style={[styles.pricingPrice, plan.popular && styles.pricingPricePopular]}>
                    {plan.price}
                  </Text>
                  <Text style={styles.pricingPeriod}>{plan.period}</Text>
                </View>

                <View style={styles.pricingFeatures}>
                  {plan.features.map((feature, fIndex) => (
                    <View key={fIndex} style={styles.pricingFeatureRow}>
                      <Icon
                        name={feature.included ? 'check' : 'close'}
                        size={18}
                        color={feature.included ? brandColors.green : brandColors.gray}
                      />
                      <Text style={[styles.pricingFeatureText, !feature.included && styles.pricingFeatureTextDisabled]}>
                        {feature.text}
                      </Text>
                    </View>
                  ))}
                </View>

                <Pressable
                  style={[
                    plan.popular ? styles.primaryButton : styles.secondaryButton,
                    plan.disabled && styles.buttonDisabled,
                    { width: '100%' },
                  ]}
                  disabled={plan.disabled}
                  onPress={handleGetStarted}
                >
                  <Text style={plan.popular ? styles.primaryButtonText : styles.secondaryButtonText}>
                    {plan.cta}
                  </Text>
                </Pressable>
              </View>
            ))}
          </View>

          <Text style={styles.pricingNote}>
            💳 Paiement sécurisé par Stripe • Annulation à tout moment • Satisfait ou remboursé 14 jours
          </Text>
          <Text style={styles.pricingDisclaimer}>
            Wealfi est un outil d'aide à la décision. Ce n'est pas un service de conseil en investissement au sens de l'AMF.
          </Text>
        </View>

        {/* FAQ Section */}
        <View style={styles.faqSection}>
          <Text style={styles.sectionTitle}>Questions fréquentes</Text>
          <View style={[styles.faqGrid, isDesktop && styles.faqGridDesktop]}>
            {[
              {
                q: "C'est vraiment gratuit ?",
                a: "Le diagnostic est 100% gratuit. Tu peux passer Premium (9,99€/mois) pour un suivi illimité, mais ce n'est pas obligatoire.",
              },
              {
                q: 'Mes données sont en sécurité ?',
                a: "Tes données transitent via une connexion chiffrée (SSL/TLS). Elles ne sont jamais vendues ni partagées. Tu peux supprimer ton compte à tout moment.",
              },
              {
                q: 'Vous avez accès à mes comptes bancaires ?',
                a: "Non, sauf si tu choisis de connecter ta banque. Dans ce cas, ça passe par Powens, établissement de paiement agréé par l'ACPR (n°16948). Wealfi ne stocke jamais tes identifiants bancaires.",
              },
              {
                q: "C'est du conseil financier ?",
                a: "Non. Wealfi est un outil d'aide à la décision, pas un service de conseil en investissement au sens de l'AMF. On t'aide à comprendre ta situation et à agir, mais la décision finale t'appartient toujours.",
              },
            ].map((faq, index) => (
              <View key={index} style={styles.faqCard}>
                <Text style={styles.faqQuestion}>{faq.q}</Text>
                <Text style={styles.faqAnswer}>{faq.a}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Founder Section */}
        <View style={styles.founderSection}>
          <View style={[styles.founderCard, isDesktop && styles.founderCardDesktop]}>
            <View style={styles.founderAvatar}>
              <Text style={styles.founderAvatarText}>👨‍💻</Text>
            </View>
            <View style={styles.founderContent}>
              <Text style={styles.founderTitle}>Salut, moi c'est Boubacar 👋</Text>
              <Text style={styles.founderText}>
                Ingénieur devenu entrepreneur, j'ai créé Wealfi parce que je déteste perdre de l'argent bêtement.
                Comme toi, je n'avais pas le temps de devenir expert en finance. Alors j'ai construit l'outil que j'aurais voulu avoir.
              </Text>
              <Text style={styles.founderText}>
                Aujourd'hui, je partage aussi mes analyses sur ma newsletter (10 000+ abonnés) et ma chaîne YouTube.
              </Text>
              <View style={styles.founderLinks}>
                <Pressable style={styles.founderLink} onPress={() => openLink(LINKS.youtube)}>
                  <Icon name="youtube" size={20} color="#FF0000" />
                  <Text style={[styles.founderLinkText, { color: '#FF0000' }]}>YouTube</Text>
                </Pressable>
                <Pressable style={styles.founderLink} onPress={() => openLink(LINKS.newsletter)}>
                  <Icon name="email-outline" size={20} color={brandColors.orange} />
                  <Text style={[styles.founderLinkText, { color: brandColors.orange }]}>Newsletter</Text>
                </Pressable>
                <Pressable style={styles.founderLink} onPress={() => openLink(LINKS.linkedin)}>
                  <Icon name="linkedin" size={20} color="#0077B5" />
                  <Text style={[styles.founderLinkText, { color: '#0077B5' }]}>LinkedIn</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </View>

        {/* Final CTA Section */}
        <View style={styles.finalCtaSection}>
          <Text style={styles.finalCtaTitle}>
            Prêt à reprendre le contrôle de tes finances ?
          </Text>
          <Text style={styles.finalCtaSubtitle}>
            3 minutes de diagnostic. Des années d'économies.
          </Text>

          <Pressable onPress={() => { trackCta('get_started', 'final_cta'); handleGetStarted(); }} style={styles.finalCtaButton}>
            <Text style={styles.finalCtaButtonText}>Commencer mon diagnostic gratuit</Text>
            <Icon name="arrow-right" size={20} color={brandColors.dark} />
          </Pressable>

          <View style={styles.finalCtaTrust}>
            {['Sans engagement', 'Sans carte bancaire', 'Connexion sécurisée'].map((item, index) => (
              <View key={index} style={styles.finalCtaTrustItem}>
                <Icon name="check-circle" size={16} color={brandColors.white} style={{ opacity: 0.8 }} />
                <Text style={styles.finalCtaTrustText}>{item}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Footer */}
        <View style={[styles.footer, isDesktop && styles.footerDesktop]}>
          <View style={[styles.footerContent, isDesktop && styles.footerContentDesktop]}>
            <View style={[styles.footerBrand, isDesktop && styles.footerBrandDesktop]}>
              <Text style={styles.footerLogo}>
                Wealfi
              </Text>
              <Text style={styles.footerTagline}>
                Ton assistant financier IA qui optimise ton argent pendant que tu vis ta vie.
              </Text>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Produit</Text>
              <Pressable onPress={() => scrollToSection('features')}>
                <Text style={styles.footerLink}>Fonctionnalités</Text>
              </Pressable>
              <Pressable onPress={() => scrollToSection('pricing')}>
                <Text style={styles.footerLink}>Tarifs</Text>
              </Pressable>
              <Pressable onPress={() => scrollToSection('demo')}>
                <Text style={styles.footerLink}>Démo</Text>
              </Pressable>
              <Text style={styles.footerLink}>FAQ</Text>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Outils Gratuits</Text>
              <Pressable onPress={() => Linking.openURL('/fr/tools')}>
                <Text style={styles.footerLink}>Tous les outils</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/tools/compound-interest-calculator')}>
                <Text style={styles.footerLink}>Intérêts Composés</Text>
              </Pressable>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Légal</Text>
              <Pressable onPress={() => Linking.openURL('/fr/terms')}>
                <Text style={styles.footerLink}>CGU</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/privacy')}>
                <Text style={styles.footerLink}>Confidentialité</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/legal')}>
                <Text style={styles.footerLink}>Mentions légales</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.footerBottom}>
            <Text style={styles.footerCopyright}>© 2026 Wealfi. Tous droits réservés.</Text>
            <Text style={styles.footerDisclaimer}>
              🔒 Wealfi ne fournit pas de conseil en investissement au sens de l'AMF
            </Text>
          </View>
        </View>
      </ScrollView>

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: brandColors.white,
    overflow: 'hidden', // Prevent horizontal scroll
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70, // Space for fixed navbar
    overflow: 'hidden', // Prevent horizontal scroll
  },

  // Fixed Navbar
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoEmoji: {
    fontSize: 20,
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },
  navLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 32,
  },
  navLink: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  navLinkButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  navLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  navActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  ctaButton: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
  },
  ctaButtonMobile: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  ctaButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },

  // Hero Section
  heroSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.white,
    // Gradient effect via background
  },
  heroSectionDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 80,
  },
  heroContent: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
  },
  heroContentDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 48,
  },
  heroLeft: {
    flex: 1,
    alignItems: 'center',
  },
  heroLeftDesktop: {
    alignItems: 'flex-start',
  },
  heroRight: {
    flex: 1,
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.white,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 24,
    gap: 8,
  },
  heroBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: brandColors.green,
  },
  heroBadgeText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  heroTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    lineHeight: 40,
    marginBottom: 16,
  },
  heroTitleDesktop: {
    fontSize: 48,
    lineHeight: 56,
    textAlign: 'left',
  },
  heroTitleAccent: {
    color: brandColors.green,
  },
  heroSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: 32,
    paddingHorizontal: 8,
  },
  heroButtons: {
    gap: 16,
    alignItems: 'center',
  },
  heroButtonsDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: brandColors.green,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
    shadowColor: brandColors.green,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 4,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.white,
  },
  secondaryButton: {
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    borderWidth: 2,
    borderColor: brandColors.green,
    backgroundColor: brandColors.white,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.green,
    textAlign: 'center',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  heroTrust: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    marginTop: 32,
  },
  trustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  trustText: {
    fontSize: 14,
    color: brandColors.gray,
  },

  // Chat Preview
  chatPreview: {
    backgroundColor: brandColors.dark,
    borderRadius: 20,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 8,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    marginBottom: 16,
  },
  chatAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chatAvatarText: {
    fontSize: 18,
  },
  chatName: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },
  chatStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  chatStatusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: brandColors.green,
  },
  chatStatusText: {
    fontSize: 12,
    color: brandColors.green,
  },
  chatMessages: {
    gap: 12,
  },
  chatBubbleAi: {
    backgroundColor: brandColors.green,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    padding: 12,
    maxWidth: '85%',
  },
  chatBubbleText: {
    fontSize: 14,
    color: brandColors.white,
    lineHeight: 20,
  },
  chatBold: {
    fontWeight: '700',
  },
  chatStats: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 8,
    padding: 10,
    marginTop: 8,
    gap: 4,
  },
  chatStatRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  chatStatLabel: {
    fontSize: 12,
    color: brandColors.white,
  },
  chatStatValue: {
    fontSize: 12,
    fontWeight: '700',
    color: brandColors.white,
  },
  chatBubbleUser: {
    backgroundColor: '#F3F4F6',
    borderRadius: 16,
    borderBottomRightRadius: 4,
    padding: 12,
    maxWidth: '75%',
    alignSelf: 'flex-end',
  },
  chatBubbleUserText: {
    fontSize: 14,
    color: brandColors.dark,
  },
  chatTyping: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  typingDots: {
    flexDirection: 'row',
    gap: 4,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: brandColors.green,
    opacity: 0.5,
  },
  typingDot1: {},
  typingDot2: {},
  typingDot3: {},
  typingText: {
    fontSize: 12,
    color: brandColors.gray,
  },

  // Problem Section
  problemSection: {
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    marginBottom: 12,
  },
  sectionSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    marginBottom: 40,
    maxWidth: 600,
  },
  titleAccent: {
    color: brandColors.green,
  },
  titleAccentOrange: {
    color: brandColors.orange,
  },
  problemGrid: {
    gap: 16,
    width: '100%',
    maxWidth: 1200,
  },
  problemGridDesktop: {
    flexDirection: 'row',
  },
  problemCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  problemEmoji: {
    fontSize: 40,
    marginBottom: 16,
  },
  problemCardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  problemCardDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
  },

  // Features Section
  featuresSection: {
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  featuresGrid: {
    gap: 16,
    width: '100%',
    maxWidth: 1200,
  },
  featuresGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  featureCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  featureCardDesktop: {
    flex: 1,
    minWidth: 250,
    padding: 24,
  },
  featureIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    textAlign: 'center',
  },
  featureDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Demo Section
  demoSection: {
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  demoGrid: {
    gap: 16,
    width: '100%',
    maxWidth: 1000,
    marginBottom: 24,
  },
  demoGridDesktop: {
    flexDirection: 'row',
  },
  demoCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 20,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  demoCardActive: {
    borderColor: brandColors.green,
    backgroundColor: brandColors.greenLight,
  },
  demoCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  demoAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  demoAvatarText: {
    fontSize: 24,
  },
  demoName: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
  },
  demoJob: {
    fontSize: 13,
    color: brandColors.gray,
  },
  demoProblem: {
    marginBottom: 16,
  },
  demoProblemLabel: {
    fontSize: 12,
    color: brandColors.gray,
    marginBottom: 4,
  },
  demoProblemValue: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.orange,
  },
  demoSavings: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  demoSavingsLabel: {
    fontSize: 12,
    color: brandColors.gray,
  },
  demoSavingsValue: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.green,
  },

  // Testimonials Section
  testimonialsSection: {
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  testimonialsGrid: {
    gap: 16,
    width: '100%',
    maxWidth: 1000,
  },
  testimonialsGridDesktop: {
    flexDirection: 'row',
  },
  testimonialCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  testimonialCardDesktop: {
    flex: 1,
    padding: 24,
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  testimonialAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  testimonialAvatarText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.white,
  },
  testimonialInfo: {
    flex: 1,
  },
  testimonialName: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
  },
  testimonialRole: {
    fontSize: 13,
    color: brandColors.gray,
  },
  testimonialSavings: {
    backgroundColor: brandColors.greenLight,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  testimonialSavingsLabel: {
    fontSize: 11,
    color: brandColors.gray,
  },
  testimonialSavingsValue: {
    fontSize: 14,
    fontWeight: '700',
    color: brandColors.green,
  },
  testimonialText: {
    fontSize: 15,
    color: brandColors.gray,
    lineHeight: 24,
  },

  // Pricing Section
  pricingSection: {
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  pricingGrid: {
    gap: 16,
    width: '100%',
    maxWidth: 1000,
    marginBottom: 24,
  },
  pricingGridDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  pricingCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 20,
    borderWidth: 2,
    borderColor: '#E5E7EB',
  },
  pricingCardDesktop: {
    flex: 1,
    padding: 24,
  },
  pricingCardPopular: {
    borderColor: brandColors.green,
  },
  pricingBadge: {
    position: 'absolute',
    top: -14,
    alignSelf: 'center',
    backgroundColor: brandColors.orange,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 16,
  },
  pricingBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: brandColors.white,
  },
  pricingName: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    marginBottom: 8,
    marginTop: 8,
  },
  pricingPriceContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 24,
  },
  pricingOriginalPrice: {
    fontSize: 18,
    color: brandColors.gray,
    textDecorationLine: 'line-through',
  },
  pricingPrice: {
    fontSize: 36,
    fontWeight: '700',
    color: brandColors.dark,
  },
  pricingPricePopular: {
    color: brandColors.green,
  },
  pricingPeriod: {
    fontSize: 14,
    color: brandColors.gray,
  },
  pricingFeatures: {
    gap: 12,
    marginBottom: 24,
  },
  pricingFeatureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pricingFeatureText: {
    fontSize: 14,
    color: brandColors.gray,
  },
  pricingFeatureTextDisabled: {
    color: '#C7C7CC',
  },
  pricingNote: {
    fontSize: 13,
    color: brandColors.gray,
    textAlign: 'center',
  },
  pricingDisclaimer: {
    fontSize: 12,
    color: brandColors.gray,
    textAlign: 'center',
    marginTop: 16,
    fontStyle: 'italic',
    paddingHorizontal: 24,
    lineHeight: 18,
  },

  // Founder Section
  founderSection: {
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  founderCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    maxWidth: 800,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  founderCardDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 32,
  },
  founderAvatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    alignSelf: 'center',
  },
  founderAvatarText: {
    fontSize: 48,
  },
  founderContent: {
    flex: 1,
  },
  founderTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 12,
  },
  founderText: {
    fontSize: 15,
    color: brandColors.gray,
    lineHeight: 24,
    marginBottom: 12,
  },
  founderLinks: {
    flexDirection: 'row',
    gap: 24,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  founderLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  founderLinkText: {
    fontSize: 14,
    fontWeight: '500',
  },

  // Final CTA Section
  finalCtaSection: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  finalCtaTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.white,
    textAlign: 'center',
    marginBottom: 12,
  },
  finalCtaSubtitle: {
    fontSize: 18,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center',
    marginBottom: 32,
  },
  finalCtaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.white,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
    marginBottom: 32,
  },
  finalCtaButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.dark,
  },
  finalCtaTrust: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 24,
  },
  finalCtaTrustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  finalCtaTrustText: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
  },

  // Trust Section
  trustSection: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    backgroundColor: brandColors.background,
  },
  trustGrid: {
    marginTop: 24,
    gap: 16,
  },
  trustGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 20,
  },
  trustCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  trustCardDesktop: {
    flex: 1,
    minWidth: 220,
    maxWidth: 280,
  },
  trustCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  trustCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  trustCardDesc: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 20,
  },

  // FAQ Section
  faqSection: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    backgroundColor: brandColors.white,
  },
  faqGrid: {
    marginTop: 24,
    gap: 16,
  },
  faqGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  faqCard: {
    backgroundColor: brandColors.background,
    borderRadius: 16,
    padding: 24,
    flex: 1,
    minWidth: 280,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 22,
  },

  // Footer
  footer: {
    backgroundColor: brandColors.dark,
    paddingHorizontal: 16,
    paddingVertical: 48,
  },
  footerDesktop: {
    paddingHorizontal: 48,
  },
  footerContent: {
    flexDirection: 'column',
    gap: 32,
    marginBottom: 32,
  },
  footerContentDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 48,
  },
  footerBrand: {
    maxWidth: 300,
  },
  footerBrandDesktop: {
    flex: 1,
    maxWidth: 400,
  },
  footerLogo: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.white,
    marginBottom: 12,
  },
  footerTagline: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 22,
  },
  footerLinks: {
    gap: 12,
  },
  footerLinksTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
    marginBottom: 8,
  },
  footerLink: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
  },
  footerBottom: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    paddingTop: 24,
    gap: 8,
  },
  footerCopyright: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
  footerDisclaimer: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
});

// Modal Styles
const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  container: {
    backgroundColor: brandColors.white,
    borderRadius: 20,
    maxHeight: '90%',
    width: '100%',
    maxWidth: 500,
    overflow: 'hidden',
  },
  containerDesktop: {
    maxWidth: 600,
  },
  demoBanner: {
    backgroundColor: brandColors.orangeLight,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  demoBannerText: {
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.orange,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  profileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  profileAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileAvatarText: {
    fontSize: 28,
  },
  profileName: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
  },
  profileJob: {
    fontSize: 14,
    color: brandColors.gray,
  },
  profileSituation: {
    fontSize: 12,
    color: brandColors.gray,
    marginTop: 2,
  },
  closeButton: {
    padding: 8,
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 14,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: brandColors.green,
  },
  tabText: {
    fontSize: 13,
    fontWeight: '500',
    color: brandColors.gray,
  },
  tabTextActive: {
    color: brandColors.green,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 20,
  },

  // Overview Tab
  overviewContent: {
    gap: 16,
  },
  scoreCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 16,
    padding: 20,
  },
  scoreHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  scoreLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  scoreBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  scoreBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  scoreValueContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 12,
  },
  scoreValue: {
    fontSize: 48,
    fontWeight: '700',
  },
  scoreMax: {
    fontSize: 20,
    fontWeight: '500',
    color: brandColors.gray,
  },
  scoreBar: {
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  savingsCard: {
    backgroundColor: brandColors.greenLight,
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
  },
  savingsLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.dark,
    marginBottom: 8,
  },
  savingsValue: {
    fontSize: 36,
    fontWeight: '700',
    color: brandColors.green,
  },
  savingsPeriod: {
    fontSize: 18,
    fontWeight: '500',
    color: brandColors.gray,
  },
  savingsSubtext: {
    fontSize: 13,
    color: brandColors.gray,
    marginTop: 8,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  metricIcon: {
    fontSize: 24,
    marginBottom: 8,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 12,
    color: brandColors.gray,
    textAlign: 'center',
  },

  // Accounts Tab
  accountsContent: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 16,
  },
  accountCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
  },
  accountHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  accountInfo: {
    flex: 1,
    gap: 4,
  },
  accountName: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.dark,
  },
  accountTypeBadge: {
    backgroundColor: '#E5E7EB',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  accountTypeText: {
    fontSize: 11,
    fontWeight: '500',
    color: brandColors.gray,
  },
  accountBalance: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
  },
  accountBalanceNegative: {
    color: '#EF4444',
  },
  accountDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  accountDetailItem: {
    flexDirection: 'row',
    gap: 4,
  },
  accountDetailLabel: {
    fontSize: 12,
    color: brandColors.gray,
  },
  accountDetailValue: {
    fontSize: 12,
    fontWeight: '600',
    color: brandColors.dark,
  },
  accountIssueBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: brandColors.orangeLight,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  accountIssueText: {
    fontSize: 12,
    fontWeight: '500',
    color: brandColors.orange,
  },

  // Actions Tab
  actionsContent: {
    gap: 12,
  },
  actionCard: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  actionPriority: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionPriorityText: {
    fontSize: 14,
    fontWeight: '700',
    color: brandColors.white,
  },
  actionContent: {
    flex: 1,
    gap: 8,
  },
  actionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.dark,
    lineHeight: 20,
  },
  actionMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  actionMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  actionImpact: {
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.green,
  },
  actionDifficultyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  actionDifficultyText: {
    fontSize: 11,
    fontWeight: '600',
  },
  actionTime: {
    fontSize: 12,
    color: brandColors.gray,
  },

  // Footer
  footer: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
  },
  footerText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.dark,
    marginBottom: 12,
  },
  footerCta: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.green,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
    marginBottom: 12,
    shadowColor: brandColors.green,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 4,
  },
  footerCtaText: {
    fontSize: 15,
    fontWeight: '600',
    color: brandColors.white,
  },
  footerNote: {
    fontSize: 12,
    color: brandColors.gray,
  },
});
